import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { DeviceService } from '../../services/device.service';
import { ConfirmationDialogComponent, ConfirmationDialogData } from './confirmation-dialog.component';

@Component({
  selector: 'app-device-management',
  templateUrl: './device-management.component.html',
  styleUrls: ['./device-management.component.scss']
})
export class DeviceManagementComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  displayedColumns: string[] = ['friendlyName', 'ipAddress', 'description', 'location', 'isActive', 'actions'];
  dataSource = new MatTableDataSource<any>();
  
  loading = false;
  showAddForm = false;
  showEditForm = false;
  editingDevice: any = null;

  deviceForm: FormGroup;

  constructor(
    private deviceService: DeviceService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {
    this.deviceForm = this.fb.group({
      ipAddress: ['', [Validators.required, Validators.pattern(/^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/)]],
      friendlyName: ['', [Validators.required, Validators.minLength(2)]],
      description: [''],
      location: [''],
      isActive: [true]
    });
  }

  ngOnInit(): void {
    this.loadDevices();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  loadDevices(): void {
    this.loading = true;
    this.deviceService.getDevices().subscribe({
      next: (response) => {
        this.dataSource.data = response.devices;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading devices:', error);
        this.snackBar.open('Error loading devices', 'Close', { duration: 3000 });
        this.loading = false;
      }
    });
  }

  showAddFormHandler(): void {
    this.showAddForm = true;
    this.showEditForm = false;
    this.editingDevice = null;
    this.deviceForm.reset({ isActive: true });
  }

  showEditFormHandler(device: any): void {
    this.showEditForm = true;
    this.showAddForm = false;
    this.editingDevice = device;
    this.deviceForm.patchValue({
      ipAddress: device.ipAddress,
      friendlyName: device.friendlyName,
      description: device.description,
      location: device.location,
      isActive: device.isActive
    });
  }

  cancelForm(): void {
    this.showAddForm = false;
    this.showEditForm = false;
    this.editingDevice = null;
    this.deviceForm.reset();
  }

  onSubmit(): void {
    if (this.deviceForm.valid) {
      this.loading = true;
      const formValue = this.deviceForm.value;

      if (this.showAddForm) {
        this.deviceService.createDevice(formValue).subscribe({
          next: (response) => {
            this.snackBar.open('Device created successfully', 'Close', { duration: 3000 });
            this.loadDevices();
            this.cancelForm();
            this.loading = false;
          },
          error: (error) => {
            console.error('Error creating device:', error);
            this.snackBar.open(error.error?.message || 'Error creating device', 'Close', { duration: 3000 });
            this.loading = false;
          }
        });
      } else if (this.showEditForm && this.editingDevice) {
        const updateData = { ...formValue, id: this.editingDevice.id };
        this.deviceService.updateDevice(updateData).subscribe({
          next: () => {
            this.snackBar.open('Device updated successfully', 'Close', { duration: 3000 });
            this.loadDevices();
            this.cancelForm();
            this.loading = false;
          },
          error: (error) => {
            console.error('Error updating device:', error);
            this.snackBar.open(error.error?.message || 'Error updating device', 'Close', { duration: 3000 });
            this.loading = false;
          }
        });
      }
    }
  }

  deleteDevice(device: any): void {
    const dialogData: ConfirmationDialogData = {
      title: 'Delete Device',
      message: `Are you sure you want to delete "${device.friendlyName}"?`
    };

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loading = true;
        this.deviceService.deleteDevice(device.id).subscribe({
          next: () => {
            this.snackBar.open('Device deleted successfully', 'Close', { duration: 3000 });
            this.loadDevices();
            this.loading = false;
          },
          error: (error) => {
            console.error('Error deleting device:', error);
            this.snackBar.open(error.error?.message || 'Error deleting device', 'Close', { duration: 3000 });
            this.loading = false;
          }
        });
      }
    });
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }
} 